import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import useStyles from "./styles";
import { Sidebar } from "../../../containers/Sidebar/Sidebar";
import { CustomButtonTable } from "../../../components/CustomButtonTable/CustomButtonTable";
import { Icon } from "../../../components/Icon/Icon";
import { VehiculeSection } from "./VehiculeSection/VehiculeSection";
import { CostSection } from "./CostSection/CostSection";
import { Documents } from "./Documents/Documents";
import { Mileage } from "./Mileage/Mileage";
import { Pneumatique } from "./Pneumatique/Pneumatique";
import { Collaborator } from "./Collaborator/Collaborator";
import { CustomTable } from "../../../components/CustomTable/CustomTable";
import { columnsClaims, columnsHistory } from "./data";
import { useNavigate, useParams } from "react-router";
import { CustomIconButton } from "../../../components/CustomIconButton/CustomIconButton";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import { useGetAllEvents } from "../../../api/event";
import { useGetAllVehiculesFleet, useDeleteVehicule } from "../../../api/vehicule";
import { useGetAllSinisters } from "../../../api/sinistre";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { EventDocumentsModal } from "./EventDocumentsModal/EventDocumentsModal";

type Props = {};

export const UpdateVehicule: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentFleet = getCurrentFleetId();

  const { id = "" } = useParams();
  const vehicleId = parseInt(id);

  // Fetch all vehicles for prev/next navigation
  const { vehicules: allVehicles } = useGetAllVehiculesFleet(currentFleet, 1, 9999);
  const sortedIds = (allVehicles ?? []).map((v: any) => v.vehicleId ?? v.id).filter(Boolean);
  const currentIdx = sortedIds.indexOf(vehicleId);
  const prevId = currentIdx > 0 ? sortedIds[currentIdx - 1] : null;
  const nextId = currentIdx >= 0 && currentIdx < sortedIds.length - 1 ? sortedIds[currentIdx + 1] : null;

  // Fetch events - removed the page and per_page parameters or use high values
  const { events } = useGetAllEvents(
    currentFleet, 
    vehicleId, 
    1, 
    100 // Increased to fetch more events
  );

  // Claims (sinistres) of this vehicle: the API returns the whole fleet's
  // sinisters, so filter down to the current vehicle.
  const { sinistres } = useGetAllSinisters(currentFleet);
  const vehicleSinisters = (sinistres ?? []).filter(
    (sinister: TSinister) => sinister.vehicle_id === vehicleId
  );

  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  const { mutate: disableVehicle, isPending: isDisabling } = useDeleteVehicule(
    currentFleet,
    vehicleId,
    () => {
      setDisableModalOpen(false);
      navigate("/vehicule/list");
    }
  );

  const handleOpenDocumentsModal = (eventId: number) => {
    setSelectedEventId(eventId);
    setDocumentsModalOpen(true);
  };

  const handleCloseDocumentsModal = () => {
    setDocumentsModalOpen(false);
    setSelectedEventId(null);
  };



  // Handler for viewing event details
  const handleViewEventDetail = (eventId: number) => {
    navigate(`/vehicule/update/${vehicleId}/event/${eventId}`);
  };

  // Handler for viewing a claim (sinistre) detail
  const handleViewClaim = (sinisterId: number) => {
    navigate(`/claims/update/${sinisterId}`);
  };

  // Keyed on `t` so the headers follow a language switch instead of staying
  // frozen at first render.
  const historyColumns = useMemo(
    () => columnsHistory(t, handleOpenDocumentsModal, handleViewEventDetail),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, vehicleId]
  );
  const claimsColumns = useMemo(
    () => columnsClaims(t, handleViewClaim),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  // Prefill the claim form with THIS vehicle so the user does not have to
  // pick it again when coming from the vehicle sheet.
  const handleAddClaim = () => {
    const cv = (allVehicles ?? []).find(
      (v: any) => (v.vehicleId ?? v.id) === vehicleId
    );
    navigate("/claims/create", {
      state: {
        presetVehicle: {
          vehicleId,
          brand: cv?.brand,
          brandModel: cv?.brandModel,
          immatriculation: cv?.immatriculation,
        },
      },
    });
  };

  if (isNaN(vehicleId) || vehicleId <= 0) {
    return (
      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div style={{ padding: 32, textAlign: "center" }}>
          <CustomTypography variant="h2" text={t("error.invalidVehicleId", "Invalid Vehicle ID")} />
          <CustomButtonTable
            variant="default"
            textVariant="link"
            label={t("common.goBack", "Go back")}
            width="fit-content"
            onClick={() => navigate("/vehicule/list")}
          />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.vehiculeRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <div className={classes.container}>
        <div className={classes.containerCar}>
          <div>
            <CustomTypography
              variant="h2"
              text={t("vehicule.vehicule", "Car")}
              lineHeight={"47px"}
            />
            <div className={classes.carIconContainer}>
              <Icon name={"vehicule"} height={24} width={24} />
              <CustomTypography variant="default" text={`ID: ${vehicleId}`} />
              <div style={{ display: "flex", gap: 4, marginLeft: 12 }}>
                <button
                  disabled={!prevId}
                  onClick={() => prevId && navigate(`/vehicule/update/${prevId}`)}
                  style={{
                    border: "1px solid rgba(148,163,184,0.3)",
                    borderRadius: 6,
                    background: prevId ? "rgba(37,99,235,0.08)" : "transparent",
                    cursor: prevId ? "pointer" : "not-allowed",
                    opacity: prevId ? 1 : 0.35,
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 13,
                    color: "inherit",
                  }}
                  title={t("common.previous", "Previous")}
                >
                  ← {t("common.previous", "Préc.")}
                </button>
                <button
                  disabled={!nextId}
                  onClick={() => nextId && navigate(`/vehicule/update/${nextId}`)}
                  style={{
                    border: "1px solid rgba(148,163,184,0.3)",
                    borderRadius: 6,
                    background: nextId ? "rgba(37,99,235,0.08)" : "transparent",
                    cursor: nextId ? "pointer" : "not-allowed",
                    opacity: nextId ? 1 : 0.35,
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 13,
                    color: "inherit",
                  }}
                  title={t("common.next", "Next")}
                >
                  {t("common.next", "Suiv.")} →
                </button>
              </div>
            </div>
          </div>
          <CustomButtonTable
            variant="default"
            textVariant={"link"}
            label={t(
              "panel.disableTheVehicle.disableTheVehicle",
              "DISABLE THE VEHICLE"
            )}
            width={"fit-content"}
            fontWeight={"bold"}
            textDecoration={"none"}
            iconName={"trash"}
            onClick={() => setDisableModalOpen(true)}
          />
        </div>
        <div className={classes.subContainer}>
          <div className={classes.subContainerLeft}>
            <VehiculeSection />
            <CostSection />
            <div className={classes.finalItem}>
              <Documents />
              <div className={classes.rightItem}>
                <Mileage />
              </div>
            </div>
          </div>
          <div className={classes.subContainerRight}>
            <div className={classes.containerPneumatique}>
              <Pneumatique />
              <Collaborator />
            </div>
            <CustomTable
              columns={historyColumns}
              data={events || []}
              tableMaxHeight={"200px"}
              tableMinHeight={"200px"}
              titleHeader={t(
                "panel.historyOfInterventions.historyOfInterventions",
                "HISTORY OF INTERVENTIONS"
              )}
              componentHeader={
                <CustomIconButton
                  iconName={"plus"}
                  widthIcon={"20px"}
                  heightIcon={"20px"}
                  onClick={() => navigate(`/vehicule/update/${vehicleId}/create-event`)}
                />
              }
              labelButtonFooter={t(
                "panel.historyOfInterventions.seeAllInterventions",
                "See all interventions"
              )}
              showPagination={false}
              onClickButtonFooter={() => navigate(`/vehicule/${vehicleId}/history`)}
            />
            <CustomTable
              titleHeader={t("panel.claims.claims", "Claims")}
              columns={claimsColumns}
              data={vehicleSinisters}
              labelButtonFooter={t(
                "panel.claims.seeAllClaims",
                "See all claims"
              )}
              componentHeader={
                <CustomIconButton
                  iconName={"plus"}
                  widthIcon={"20px"}
                  heightIcon={"20px"}
                  onClick={handleAddClaim}
                />
              }
              tableMaxHeight={"228px"}
              tableMinHeight={"228px"}
              showPagination={false}
              onClickButtonFooter={() => navigate("/claims/list")}
            />
          </div>
        </div>
      </div>

      <CustomModal
        open={disableModalOpen}
        title={t("panel.disableTheVehicle.disableTheVehicle", "DISABLE THE VEHICLE")}
        isConfirmation
        textConfirmation={t("common.confirm", "Confirm")}
        loadingSave={isDisabling}
        onClose={() => setDisableModalOpen(false)}
        onCancel={() => setDisableModalOpen(false)}
        onConfirm={() => disableVehicle()}
      >
        <div style={{ padding: "8px 4px 16px 4px" }}>
          <CustomTypography
            variant="default"
            text={t(
              "panel.disableTheVehicle.confirmMessage",
              "Are you sure you want to disable this vehicle? It will be removed from the active fleet."
            )}
          />
        </div>
      </CustomModal>

      {selectedEventId !== null && (
        <EventDocumentsModal
          open={documentsModalOpen}
          onClose={handleCloseDocumentsModal}
          vehicleId={vehicleId}
          eventId={selectedEventId}
        />
      )}
    </Sidebar>
  );
};